import React from 'react';
import { COUNTRY_GEO } from './types';

const W = 1000;
const H = 640;
const PAD_X = 80;
const PAD_Y = 64;
const LON_MIN = -118;
const LON_MAX = 8;
const LAT_MIN = -44;
const LAT_MAX = 52;

const projX = (lon: number) => PAD_X + ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * (W - 2 * PAD_X);
const projY = (lat: number) => PAD_Y + ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * (H - 2 * PAD_Y);

// Ajustes finos de la etiqueta por país para evitar solapes (cono sur apretado).
const LABEL: Record<string, { dx: number; dy: number; anchor: 'start' | 'middle' | 'end' }> = {
  'España': { dx: 0, dy: -46, anchor: 'middle' },
  'México': { dx: -14, dy: -30, anchor: 'end' },
  'Colombia': { dx: -18, dy: 4, anchor: 'end' },
  'Perú': { dx: -16, dy: 4, anchor: 'end' },
  'Chile': { dx: -16, dy: 6, anchor: 'end' },
  'Argentina': { dx: 16, dy: 40, anchor: 'start' },
  'Uruguay': { dx: 18, dy: 6, anchor: 'start' },
  'República Dominicana': { dx: 16, dy: -6, anchor: 'start' },
};

interface Props {
  countries: { name: string; count: number }[];
  active: boolean;
}

const WorldMap: React.FC<Props> = ({ countries, active }) => {
  const known = countries.filter((c) => COUNTRY_GEO[c.name]);
  const es = COUNTRY_GEO['España'];
  const esX = projX(es.lon);
  const esY = projY(es.lat);

  const gridLons = [-110, -90, -70, -50, -30, -10];
  const gridLats = [40, 20, 0, -20, -40];

  return (
    <svg
      className={`wrapped-map${active ? ' play' : ''}`}
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Mapa de países participantes: España y Latinoamérica"
    >
      <defs>
        <linearGradient id="wArcGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffd702" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#ffd702" stopOpacity="0.15" />
        </linearGradient>
        <radialGradient id="wGlow" cx="50%" cy="42%" r="60%">
          <stop offset="0%" stopColor="#0a4b7a" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#0a4b7a" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* resplandor de fondo */}
      <rect x="0" y="0" width={W} height={H} fill="url(#wGlow)" />

      {/* retícula geográfica sutil */}
      {gridLons.map((lon) => (
        <line key={`v${lon}`} className="grid-line" x1={projX(lon)} y1={PAD_Y - 20} x2={projX(lon)} y2={H - PAD_Y + 20} />
      ))}
      {gridLats.map((lat) => (
        <line key={`h${lat}`} className="grid-line" x1={PAD_X - 30} y1={projY(lat)} x2={W - PAD_X + 30} y2={projY(lat)} />
      ))}

      {/* arcos España -> cada país */}
      {known
        .filter((c) => c.name !== 'España')
        .map((c, i) => {
          const g = COUNTRY_GEO[c.name];
          const x2 = projX(g.lon);
          const y2 = projY(g.lat);
          const mx = (esX + x2) / 2;
          const my = (esY + y2) / 2;
          // curva el control point hacia arriba para un arco elegante
          const dist = Math.hypot(x2 - esX, y2 - esY);
          const cx = mx;
          const cy = my - dist * 0.28;
          return (
            <path
              key={`arc-${c.name}`}
              className="arc"
              d={`M ${esX} ${esY} Q ${cx} ${cy} ${x2} ${y2}`}
              style={{ animationDelay: `${0.35 + i * 0.16}s` }}
            />
          );
        })}

      {/* nodos + etiquetas */}
      {known.map((c) => {
        const g = COUNTRY_GEO[c.name];
        const x = projX(g.lon);
        const y = projY(g.lat);
        const r = 7 + Math.sqrt(c.count) * 2.4;
        const lab = LABEL[c.name] || { dx: 12, dy: 4, anchor: 'start' as const };
        const isEs = c.name === 'España';
        return (
          <g key={`node-${c.name}`}>
            <circle className="node-halo" cx={x} cy={y} r={r * 1.85} />
            <circle className={`node-core${isEs ? ' es' : ''}`} cx={x} cy={y} r={r} />
            <text className="node-label" x={x + lab.dx} y={y + lab.dy} textAnchor={lab.anchor}>
              {c.name}
              <tspan dx="6" style={{ fill: '#ffd702' }}>{c.count}</tspan>
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default WorldMap;
